import React, { useState, useEffect, useRef } from 'react';
import { Typography, Container, Grid, Paper, Box, Button } from '@mui/material';
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { MAPBOX_ACCESS_TOKEN } from '../mapboxConfig';
import { Link } from 'react-router-dom';

mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN || '';

interface JobApplication {
  id: number;
  company: string;
  position: string;
  location: string;
  salary: string;
  status: string;
  dateapplied: string;
}

const Dashboard: React.FC<{ userName: string }> = ({ userName }) => {
  const [applications, setApplications] = useState<JobApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [geocodedLocations, setGeocodedLocations] = useState<{ [key: string]: [number, number] }>({});
  const [locationCounts, setLocationCounts] = useState<{ [key: string]: number }>({});

  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const mapInitializedRef = useRef(false);
  const markersRef = useRef<mapboxgl.Marker[]>([]);

  const customStyles = `
    .mapboxgl-popup {
    z-index: 1;
  }

  .mapboxgl-popup-content {
    pointer-events: auto;
  }
  `;
  // Add this to your component, before the return statement
  useEffect(() => {
    const styleElement = document.createElement('style');
    styleElement.textContent = customStyles;
    document.head.appendChild(styleElement);

    return () => {
      document.head.removeChild(styleElement);
    };
  }, []);

  useEffect(() => {
    fetchApplications();
  }, []);

  useEffect(() => {
    if (mapContainer.current && !map.current) {
      try {
        initializeMap();
      } catch (error) {
        console.error('Error initializing map:', error);
        setError('Failed to initialize the map. Please try again.');
      }
    }
  }, [mapContainer.current]);

  useEffect(() => {
    if (mapInitializedRef.current && Object.keys(geocodedLocations).length > 0) {
      createMarkers(geocodedLocations, locationCounts);
    }
  }, [geocodedLocations, locationCounts]);

  const fetchApplications = async () => {
    try {
      // const response = await fetch('http://localhost:5001/api/applications', {
      const response = await fetch(`${process.env.REACT_APP_API_BASE_URL}/api/applications`, {
        headers: {
          'x-auth-token': localStorage.getItem('token') || '',
        },
      });
      if (!response.ok) throw new Error('Failed to fetch applications');
      const data = await response.json();
      setApplications(data.applications);
      processLocations(data.applications);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching applications:', error);
      setError('Failed to load applications. Please try again.');
      setLoading(false);
    }
  };

  const processLocations = async (apps: JobApplication[]) => {
    const counts: { [key: string]: number } = {};
    apps.forEach(app => {
      if (app.location) {
        const cityName = app.location.split(',')[0].trim(); // Extract only the city name
        counts[cityName] = (counts[cityName] || 0) + 1;
      }
    });
    setLocationCounts(counts);

    const locations = Object.keys(counts);
    const geocodePromises = locations.map(location => geocodeLocation(location));
    const geocodedResults = await Promise.all(geocodePromises);

    const geocoded: { [key: string]: [number, number] } = {};
    geocodedResults.forEach((result, index) => {
      if (result) {
        geocoded[locations[index]] = result;
      }
    });
    setGeocodedLocations(geocoded);
  };

  const geocodeLocation = async (location: string): Promise<[number, number] | null> => {
    const endpoint = `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(location)}.json`;
    const params = new URLSearchParams({
      access_token: MAPBOX_ACCESS_TOKEN || '',
      types: 'place',
      limit: '1',
    });

    try {
      const response = await fetch(`${endpoint}?${params}`);
      const data = await response.json();
      if (data.features && data.features.length > 0) {
        return data.features[0].center;
      }
      return null;
    } catch (error) {
      console.error(`Error geocoding ${location}:`, error);
      return null;
    }
  };

  const initializeMap = () => {
    if (mapContainer.current && !map.current) {
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: [-98.5795, 39.8283],
        zoom: 3,
      });
      mapInitializedRef.current = true;
      window.addEventListener('resize', () => map.current?.resize());
    }
  };

  const createMarkers = (
    geocodedLocations: { [key: string]: [number, number] },
    locationCounts: { [key: string]: number }
  ) => {
    if (!map.current) return;
  
    markersRef.current.forEach(marker => marker.remove());
    markersRef.current = [];
  
    const coordinatesArray: [number, number][] = [];
    let remoteCount = 0;
  
    Object.entries(geocodedLocations).forEach(([location, coordinates]) => {
      const count = locationCounts[location];
      if (location.toLowerCase() === 'remote') {
        remoteCount = count;
        return;
      }
  
      const el = document.createElement('div');
      el.className = 'marker';
      el.style.width = '18px';
      el.style.height = '18px';
      el.style.borderRadius = '50%';
      el.style.background = 'linear-gradient(to right, #3a7bd5, #00d2ff)';
      el.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';
  
      const popupContent = document.createElement('div');
      popupContent.style.fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';
      popupContent.style.padding = '10px';
      popupContent.style.borderRadius = '8px';
      popupContent.style.background = 'white';
      popupContent.style.width = '150px';
      popupContent.innerHTML = `
        <strong style="color: #3a7bd5; font-size: 14px; display: block; margin-bottom: 5px;">${location}</strong>
        <span style="color: #666; font-size: 12px; display: block; margin-bottom: 5px;">Applications: ${count}</span>
        <button class="show-more-btn" style="
          background: linear-gradient(to right, #3a7bd5, #00d2ff);
          color: white;
          border: none;
          padding: 5px 10px;
          border-radius: 4px;
          cursor: pointer;
          font-size: 12px;
          transition: background 0.3s;
          width: 100%;
        ">Show More</button>
      `;
      popupContent.querySelector('.show-more-btn')?.addEventListener('click', (e) => {
        e.stopPropagation();
        window.location.href = `/applications/${encodeURIComponent(location)}`;
      });
      popupContent.querySelector('.show-more-btn')?.addEventListener('mouseenter', (e) => {
        (e.target as HTMLElement).style.background = 'linear-gradient(to right, #3a7bd5, #3a7bd5)';
      });
      popupContent.querySelector('.show-more-btn')?.addEventListener('mouseleave', (e) => {
        (e.target as HTMLElement).style.background = 'linear-gradient(to right, #3a7bd5, #00d2ff)';
      });
  
      const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
        offset: [0, -9],
        className: 'custom-popup'
      }).setDOMContent(popupContent);
  
      const marker = new mapboxgl.Marker(el)
        .setLngLat(coordinates)
        .setPopup(popup)
        .addTo(map.current!);
  
      let hideTimeout: NodeJS.Timeout;
  
      const showPopup = () => {
        clearTimeout(hideTimeout);
        popup.addTo(map.current!);
      };
  
      const hidePopup = () => {
        hideTimeout = setTimeout(() => {
          popup.remove();
        }, 300);
      };
  
      el.addEventListener('mouseenter', showPopup);
      el.addEventListener('mouseleave', hidePopup);
      
      popup.on('open', () => {
        const popupElement = popup.getElement();
        if (popupElement) {
          popupElement.addEventListener('mouseenter', () => {
            clearTimeout(hideTimeout);
          });
          popupElement.addEventListener('mouseleave', hidePopup);
        }
      });
  
      markersRef.current.push(marker);
      coordinatesArray.push(coordinates);
    });
  
    if (coordinatesArray.length > 0) {
      const bounds = coordinatesArray.reduce((bounds, coord) => {
        return bounds.extend(coord);
      }, new mapboxgl.LngLatBounds(coordinatesArray[0], coordinatesArray[0]));
  
      map.current!.fitBounds(bounds, { padding: 50 });
    }
  
    if (remoteCount > 0) {
      createRemoteSatellite(remoteCount);
    }
  };

  const createRemoteSatellite = (count: number) => {
    if (!mapContainer.current) return;

    const satelliteEl = document.createElement('div');
    satelliteEl.className = 'remote-satellite';
    satelliteEl.style.position = 'absolute';
    satelliteEl.style.top = '10px';
    satelliteEl.style.left = '10px';
    satelliteEl.style.width = '18px';
    satelliteEl.style.height = '18px';
    satelliteEl.style.borderRadius = '50%';
    satelliteEl.style.background = 'linear-gradient(to right, #3a7bd5, #00d2ff)';
    satelliteEl.style.boxShadow = '0 0 10px rgba(0,0,0,0.3)';

    const popupEl = document.createElement('div');
    popupEl.className = 'remote-popup';
    popupEl.style.position = 'absolute';
    popupEl.style.top = '35px';
    popupEl.style.left = '10px';
    popupEl.style.backgroundColor = 'white';
    popupEl.style.padding = '10px';
    popupEl.style.borderRadius = '8px';
    popupEl.style.boxShadow = '0 2px 4px rgba(0,0,0,0.2)';
    popupEl.style.display = 'none';
    popupEl.style.fontFamily = '"Roboto", "Helvetica", "Arial", sans-serif';
    popupEl.style.width = '150px';
    popupEl.innerHTML = `
      <strong style="color: #3a7bd5; font-size: 14px; display: block; margin-bottom: 5px;">Remote</strong>
      <span style="color: #666; font-size: 12px; display: block; margin-bottom: 5px;">Applications: ${count}</span>
      <button class="show-more-btn" style="
        background: linear-gradient(to right, #3a7bd5, #00d2ff);
        color: white;
        border: none;
        padding: 5px 10px;
        border-radius: 4px;
        cursor: pointer;
        font-size: 12px;
        transition: background 0.3s;
        width: 100%;
      ">Show More</button>
    `;

    popupEl.querySelector('.show-more-btn')?.addEventListener('click', (e) => {
      e.stopPropagation();
      window.location.href = '/applications/Remote';
    });
    popupEl.querySelector('.show-more-btn')?.addEventListener('mouseenter', (e) => {
      (e.target as HTMLElement).style.background = 'linear-gradient(to right, #3a7bd5, #3a7bd5)';
    });
    popupEl.querySelector('.show-more-btn')?.addEventListener('mouseleave', (e) => {
      (e.target as HTMLElement).style.background = 'linear-gradient(to right, #3a7bd5, #00d2ff)';
    });

    let hideTimeout: NodeJS.Timeout;

    const showPopup = () => {
      clearTimeout(hideTimeout);
      popupEl.style.display = 'block';
    };

    const hidePopup = () => {
      hideTimeout = setTimeout(() => {
        popupEl.style.display = 'none';
      }, 300);
    };

    satelliteEl.addEventListener('mouseenter', showPopup);
    satelliteEl.addEventListener('mouseleave', hidePopup);
    popupEl.addEventListener('mouseenter', () => clearTimeout(hideTimeout));
    popupEl.addEventListener('mouseleave', hidePopup);

    mapContainer.current.appendChild(satelliteEl);
    mapContainer.current.appendChild(popupEl);
  };

  const statusCounts = applications.reduce((acc, app) => {
    acc[app.status] = (acc[app.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);


  const barChartData = Object.entries(locationCounts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, 5); // Show only top 5 locations

  const STATUS_COLORS = {
    Applied: '#3a7bd5',
    Interviewing: '#00d2ff',
    Hired: '#4caf50',
    Rejected: '#f44336'
  };

  const CustomizedLegend = ({ payload }: { payload: any[] }) => {
    if (!payload || payload.length === 0) {
      return null;
    }

    const total = payload.reduce((sum, entry) => sum + (entry.value || 0), 0);

    return (
      <Box sx={{ mt: 2 }}>
        {payload.map((entry, index) => {
          const percentage = total > 0 ? ((entry.value / total) * 100).toFixed(0) : '0';
          return (
            <Box key={`item-${index}`} sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Box sx={{ width: 12, height: 12, backgroundColor: entry.color, mr: 1 }} />
              <Typography variant="body2" sx={{ mr: 1, minWidth: '80px' }}>{entry.name}</Typography>
              <Typography variant="body2" sx={{ mr: 1 }}>{entry.value}</Typography>
              <Typography variant="body2" color="text.secondary">
                ({percentage}%)
              </Typography>
            </Box>
          );
        })}
      </Box>
    );
  };

  const pieChartData = Object.entries(statusCounts).map(([name, value]) => ({
    name,
    value,
    color: STATUS_COLORS[name as keyof typeof STATUS_COLORS] || '#000000'
  }));


  if (loading) return <Typography>Loading dashboard data...</Typography>;
  if (error) return <Typography color="error">{error}</Typography>;

  if (applications.length === 0) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#3a7bd5', mb: 3 }}>
          Welcome back, {userName}!
        </Typography>
        <Paper sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', textAlign: 'center' }}>
          <Typography variant="h6" gutterBottom sx={{ color: '#3a7bd5', mb: 2 }}>
            You have no applications yet.
          </Typography>
          <Typography variant="body1" sx={{ mb: 3 }}>
            Start tracking your job search by adding your first application or importing existing data.
          </Typography>
          <Button
            component={Link}
            to="/add"
            variant="contained"
            sx={{
              background: 'linear-gradient(to right, #3a7bd5, #00d2ff)',
              color: 'white',
              '&:hover': {
                background: 'linear-gradient(to right, #3a7bd5, #3a7bd5)',
              },
            }}
          >
            Add Your First Application
          </Button>
        </Paper>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Typography variant="h4" component="h1" gutterBottom sx={{ color: '#3a7bd5', mb: 3 }}>
        Welcome back, {userName}!
      </Typography>
      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#3a7bd5', mb: 2 }}>
              Locations
            </Typography>
            <div ref={mapContainer} style={{ width: '100%', height: 400, borderRadius: '8px', position: 'relative' }} />
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{
            p: 3,
            borderRadius: 2,
            height: '100%',
            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
            display: 'flex',
            flexDirection: 'column'
          }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#3a7bd5', mb: 2 }}>
              Status
            </Typography>
            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie
                    data={pieChartData}
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={5}
                    dataKey="value"
                  >
                    {pieChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={STATUS_COLORS[entry.name as keyof typeof STATUS_COLORS] || '#000000'} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
              <CustomizedLegend payload={pieChartData} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#3a7bd5', mb: 2 }}>
              Top 5 Locations
            </Typography>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={barChartData}>
                <XAxis dataKey="name" />
                <YAxis />
                <Tooltip />
                <Legend />
                <Bar dataKey="value" fill="#3a7bd5" />
              </BarChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 2, height: '100%', boxShadow: '0 4px 6px rgba(0,0,0,0.1)', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
            <Typography variant="h6" gutterBottom sx={{ color: '#3a7bd5', mb: 2 }}>
              Total Applications
            </Typography>
            <Typography variant="h2" component="div" sx={{ color: '#3a7bd5', fontWeight: 'bold' }}>
              {applications.length}
            </Typography>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Dashboard;